import { useTranslation } from 'react-i18next'
import { CalendarPlus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useNetwork } from '@/contexts/NetworkContext'
import { budgetTotal, isCurrentPeriod, type BudgetPeriod } from '@/services/lineItems/budgets'
import { formatCalendarDay } from '@/utils/format'
import { formatCurrencyAmount } from '@/utils/money'
import { cn } from '@/lib/utils'

export interface BudgetPeriodsTableProps {
  periods: BudgetPeriod[]
  /** Whether what one period leaves unspent carries into the next one. */
  rollover?: boolean
  /** Opens the editor. Left out on a read-only view. */
  onEdit?: () => void
  /** Label for the edit button — "Update budget", "Add budget periods", … */
  editLabel?: string
  editDisabled?: boolean
  /** Shown under the button when editing is not possible yet. */
  hint?: string
}

/**
 * A budget split over periods, as it stands: what each period may spend, what
 * it has spent, and — under rollover — what the unspent remainder of the
 * period before left it to spend. The periods themselves are edited in
 * `BudgetPeriodsDialog`, since changing them moves the object's own dates.
 */
export function BudgetPeriodsTable({
  periods,
  rollover,
  onEdit,
  editLabel,
  editDisabled,
  hint,
}: BudgetPeriodsTableProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const total = budgetTotal(periods)

  return (
    // The table takes the full width; the button below keeps its own size.
    <div className="flex flex-col gap-2">
      {/* Framed like the stats panel's metric table, so it reads as a card of its own. */}
      {periods.length > 0 && (
        <div className="overflow-hidden rounded-lg border bg-(--ad-surface-raised)">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('budgets.dates')}</TableHead>
                <TableHead className="text-right">{t('budgets.amount')}</TableHead>
                {rollover && (
                  <TableHead className="text-right">{t('budgets.adjustedAmount')}</TableHead>
                )}
                <TableHead className="text-right">{t('budgets.spend')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {periods.map((period) => (
                <TableRow
                  key={period.startDay}
                  // The period being spent right now is the one people look for.
                  className={cn(isCurrentPeriod(period, timeZone) && 'font-medium')}
                >
                  <TableCell>
                    {formatCalendarDay(period.startDay)} – {formatCalendarDay(period.endDay)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrencyAmount(period.amount, 2)}
                  </TableCell>
                  {rollover && (
                    <TableCell className="text-right tabular-nums">
                      {formatCurrencyAmount(period.adjustedAmount, 2)}
                    </TableCell>
                  )}
                  <TableCell className="text-right tabular-nums">
                    {formatCurrencyAmount(period.spend, 2)}
                    {period.priorSpend && (
                      <div className="text-xs text-(--ad-text-muted)">
                        {t('budgets.includesPriorSpend', {
                          amount: formatCurrencyAmount(period.priorSpend, 2),
                        })}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell>{t('budgets.total')}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCurrencyAmount(total, 2)}
                </TableCell>
                {rollover && <TableCell />}
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      )}

      {rollover && periods.length > 0 && (
        <p className="text-xs text-(--ad-text-muted)">{t('budgets.rolloverExplainer')}</p>
      )}

      {onEdit && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={editDisabled}
          onClick={onEdit}
        >
          <CalendarPlus className="size-4" />
          {editLabel ?? t('budgets.updateBudget')}
        </Button>
      )}
      {hint && <p className="text-xs text-(--ad-text-muted)">{hint}</p>}
    </div>
  )
}
